import React, { Component, Fragment, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { observer } from 'mobx-react';
import Loading from 'react-simple-loading';

import { loadTranslations } from 'services/i18nService';
import {
  JS_ERROR_CATCH,
  UPDATE_BALANCE,
} from 'services/MessagingService/EventNames';
import createMessagingService from 'services/MessagingService/MessagingService';
import trackingService from 'services/UITrackingService';
import utmTracking from 'services/utmTracking';
import VWOService from 'services/VWOService';
import ProfileStore from 'stores/ProfileStore';
import NotificationRenderer from 'pages/NotificationRenderer/NotificationRenderer';
import LoginFlowV2ReduxDispatcher from 'components/LoginFlowV2ReduxDispatcher/LoginFlowV2ReduxDispatcher';
import WeakPasswordControlRoom from 'components/WeakPasswordControlRoom/WeakPasswordControlRoom';
import { PARSED_UI_TRACE_ERRORS } from 'enum/profile';
import {
  actionCreators as applicationActions,
  selector as appSelector,
} from 'app/AppState';
import {
  actionCreators as paymentActions,
  selector as paymentSelector,
} from 'features/Payment/State';
import {
  actionCreators as listActions,
  selector,
} from 'pages/Drawer/State';
import {
  actionCreators as userListActions,
  selector as userSelector,
} from 'pages/User/State';
import {
  actionCreators as basicInfoActions,
  selector as basicInfoSelector,
} from 'components/BasicInfo/State';

import errorImg from '../../images/errorImg.jpeg';

import EmailVerifiedNotification from './components/EmailVerifiedNotification';
import Confirm2FA from './components/Confim2FA';
import Main from './Main';
import styles from './App.css';


const BuyTokensModal = lazy(() => import('pages/BuyTokensModal/BuyTokensModal'));
const ModalRenderer = lazy(() => import('components/ModalRenderer/ModalRenderer'));

@withRouter
@connect(basicInfoSelector, (dispatch) => {
  return { basicInfoActions: bindActionCreators(basicInfoActions, dispatch) };
})
@connect(appSelector, (dispatch) => {
  return { appActions: bindActionCreators(applicationActions, dispatch) };
})
@connect(selector, (dispatch) => {
  return {
    drawerActions: bindActionCreators(listActions, dispatch),
    dispatch,
  };
})
@connect(userSelector, (dispatch) => {
  return { userActions: bindActionCreators(userListActions, dispatch) };
})
@connect(paymentSelector, (dispatch) => {
  return { paymentActions: bindActionCreators(paymentActions, dispatch) };
})
@observer
export default class App extends Component {
  static propTypes = {
    appActions: PropTypes.object.isRequired,
    basicInfoActions: PropTypes.any.isRequired,
    dispatch: PropTypes.func.isRequired,
    drawer: PropTypes.any.isRequired,
    drawerActions: PropTypes.any.isRequired,
    user: PropTypes.object.isRequired,
    userActions: PropTypes.any.isRequired,
  };

  constructor(...args) {
    super(...args);

    trackingService.emit('domain');
    this.state = { loaded: false, hasError: false };
    this.messagingService = createMessagingService();

    if (PARSED_UI_TRACE_ERRORS) {
      this.overrideConsoleError();
    }

    utmTracking.initialize();
    VWOService.startService();
  }

  componentDidMount() {
    const { userActions } = this.props;

    document.addEventListener('click', this.onDocumentInteract);
    document.addEventListener('keypress', this.onDocumentInteract);

    if (PARSED_UI_TRACE_ERRORS) {
      window.addEventListener('unhandledrejection', this.onUnhandledRejection);
      window.addEventListener('error', this.onErrorJs);
    }

    if (!this.state.loaded) {
      Promise.all([userActions.getUserState(), loadTranslations()])
        .then(() => this.handleSuccess())
        .catch(() => this.handleError());
    }


    this.messagingService.on(UPDATE_BALANCE, this.handleUpdateBalance);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onDocumentInteract);
    document.removeEventListener('keypress', this.onDocumentInteract);

    if (PARSED_UI_TRACE_ERRORS) {
      window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
      window.removeEventListener('error', this.onErrorJs);
    }
  }

  handleUpdateBalance = (balance) => {
    const { dispatch, appActions } = this.props;
    if (!isNaN(balance)) {
      dispatch(appActions.updateBalance(balance));
    }
  };

  overrideConsoleError() {
    /* eslint-disable no-console */
    if (!console.defaultError) {
      console.defaultError = console.error.bind(console);
    }

    console.error = (...args) => {
      this.messagingService.emit(JS_ERROR_CATCH, args);

      console.defaultError(...args);
    };
    /* eslint-enable */
  }

  handleSuccess = () => this.setState({ loaded: true, hasError: false });

  handleError = () => {
    this.setState(
      { hasError: true },
      () => {
        document.body.style = 'background: white;';
      }
    );
  };

  onErrorJs = (event) => {
    this.messagingService.emit(JS_ERROR_CATCH, {
      msg: event.message || '',
      stack: event.error?.stack || '',
      line: event.lineno || '',
      file: event.filename || '',
    });
  };

  onUnhandledRejection = (e) => {
    throw new Error(e.reason.stack);
  };

  onDocumentInteract = () => {
    window.c2UserInteracted = true;

    document.removeEventListener('click', this.onDocumentInteract);
    document.removeEventListener('keypress', this.onDocumentInteract);
  };

  renderError() {
    return (
      <div className={styles.imgContainer}>
        <img alt="error" className={styles.errorImg} src={errorImg} />
      </div>
    );
  }

  render() {
    const { loaded, hasError } = this.state;
    const { dispatch } = this.props;
    const { inTheatreMode } = ProfileStore;

    if (hasError) {
      return this.renderError();
    }
    if (!loaded) {
      return <Loading />;
    }

    return (
      <Fragment>
        <Main {...this.props} />

        <Confirm2FA />

        <Suspense>
          <BuyTokensModal dispatch={dispatch} />
        </Suspense>

        <Suspense fallback={<Fragment />}>
          <ModalRenderer />
        </Suspense>

        {!inTheatreMode && <NotificationRenderer />}
        <EmailVerifiedNotification />

        <LoginFlowV2ReduxDispatcher />
        <WeakPasswordControlRoom />
      </Fragment>
    );
  }
}
